import apiClient from "@services/api/apiClient";

/**
 * Factory for the shared Zustand `rerank` action used by every rankable entity
 * store (help topics, favourites, favourite collections, apps, widgets, zones,
 * assets...). Centralises the one URL contract so per-store implementations
 * cannot drift.
 *
 * Builds `POST {apiPaths.update}/{id}/rerank?[rank=X&]...extraParams`:
 * - The `rank` query param is omitted when `predecessorRank` is null/undefined,
 *   which the backend interprets as "place at the start".
 * - `extraParams` carries entity-specific scope IDs (`parentID`,
 *   `appCategoryID`, `favouriteCollectionID`, ...); null/undefined entries are
 *   dropped so callers can pass them unconditionally.
 *
 * On success the API response is handed to `applyResult(id, data)` (optional)
 * so the caller can sync local state, then `callbacks.onSuccess(data)` runs.
 * On failure `callbacks.onError(error)` runs and the error is rethrown so
 * callers can react (toast, revert optimistic UI, etc).
 *
 * @param {object} cfg
 * @param {() => { update: string }} cfg.getApiPaths reads the (possibly
 *   type-scoped) apiPaths at call time, not at factory-build time
 * @param {(id: string|number, data: any) => void} [cfg.applyResult] optional
 *   local-state sync from the API response
 * @returns {(id: string|number, predecessorRank: number|null,
 *   extraParams?: object, callbacks?: { onSuccess?: Function, onError?: Function })
 *   => Promise<any>}
 */
export const createRerankAction =
  ({ getApiPaths, applyResult }) =>
  async (id, predecessorRank, extraParams = {}, callbacks = {}) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(extraParams)) {
      if (value !== null && value !== undefined) {
        params.append(key, value);
      }
    }
    if (predecessorRank !== null && predecessorRank !== undefined) {
      params.append("rank", predecessorRank);
    }
    const qs = params.toString();
    const url = `${getApiPaths().update}/${id}/rerank${qs ? `?${qs}` : ""}`;

    try {
      const result = await apiClient.post(url);
      const data = result?.data ?? result;
      applyResult?.(id, data);
      callbacks.onSuccess?.(data);
      return data;
    } catch (error) {
      callbacks.onError?.(error);
      throw error;
    }
  };
